import dayjs from 'dayjs';

import { DATE_SIMPLE } from '@cast/constants';
import {
  formatCurrency,
  toCsv,
  sumDataTransferCost,
  sumDataTransferTraffic,
} from '@cast/utils';

import { DataTransferTimeSeries } from '../../../../cost-report/types/networkCostOverTime';

export const prepareDataFormExport = (data: DataTransferTimeSeries): string => {
  const formatCost = (value: number | string): string =>
    formatCurrency(+value, { maximumFractionDigits: 6 });
  const formatTraffic = (value: number | string): string => `${value}`;

  const csvData = data.map((datapoint) => {
    const totalEgressCost = sumDataTransferCost(datapoint.egress);
    const totalEgressTraffic = sumDataTransferTraffic(datapoint.egress);

    const totalIngressCost = sumDataTransferCost(datapoint.ingress);
    const totalIngressTraffic = sumDataTransferTraffic(datapoint.ingress);

    return {
      date: datapoint.timestamp,

      egressIntraAzCost: datapoint.egress.intraZoneCost,
      egressIntraAzTraffic: datapoint.egress.intraZoneBytes,
      egressInterAzCost: datapoint.egress.interZoneCost,
      egressInterAzTraffic: datapoint.egress.interZoneBytes,
      egressInternetCost: datapoint.egress.internetCost,
      egressInternetTraffic: datapoint.egress.internetBytes,
      egressCrossRegionCost: datapoint.egress.interRegionCost,
      egressCrossRegionTraffic: datapoint.egress.interRegionBytes,
      egressCloudApiCost: datapoint.egress.cloudApiCost,
      egressCloudApiTraffic: datapoint.egress.cloudApiBytes,

      ingressIntraAzCost: datapoint.ingress.intraZoneCost,
      ingressIntraAzTraffic: datapoint.ingress.intraZoneBytes,
      ingressInterAzCost: datapoint.ingress.interZoneCost,
      ingressInterAzTraffic: datapoint.ingress.interZoneBytes,
      ingressInternetCost: datapoint.ingress.internetCost,
      ingressInternetTraffic: datapoint.ingress.internetBytes,
      ingressCrossRegionCost: datapoint.ingress.interRegionCost,
      ingressCrossRegionTraffic: datapoint.ingress.interRegionBytes,
      ingressCloudApiCost: datapoint.ingress.cloudApiCost,
      ingressCloudApiTraffic: datapoint.ingress.cloudApiBytes,

      totalEgressCost,
      totalEgressTraffic,

      totalIngressCost,
      totalIngressTraffic,

      totalCost: totalEgressCost + totalIngressCost,
      totalTraffic: totalEgressTraffic + totalIngressTraffic,
    };
  });

  return toCsv(
    {
      date: {
        header: 'Date',
        map: (value) => dayjs(value).format(DATE_SIMPLE),
      },
      egressIntraAzTraffic: {
        header: 'Egress intra AZ traffic',
        map: formatTraffic,
      },
      egressInterAzTraffic: {
        header: 'Egress inter AZ traffic',
        map: formatTraffic,
      },
      egressInternetTraffic: {
        header: 'Egress internet traffic',
        map: formatTraffic,
      },
      egressCrossRegionTraffic: {
        header: 'Egress cross-region traffic',
        map: formatTraffic,
      },
      egressCloudApiTraffic: {
        header: 'Egress cloud API traffic',
        map: formatTraffic,
      },
      totalEgressTraffic: {
        header: 'Total egress traffic',
        map: formatTraffic,
      },
      egressIntraAzCost: {
        header: 'Egress intra AZ traffic cost',
        map: formatCost,
      },
      egressInterAzCost: {
        header: 'Egress inter AZ traffic cost',
        map: formatCost,
      },
      egressInternetCost: {
        header: 'Egress internet traffic cost',
        map: formatCost,
      },
      egressCrossRegionCost: {
        header: 'Egress cross-region traffic cost',
        map: formatCost,
      },
      egressCloudApiCost: {
        header: 'Egress cloud API traffic cost',
        map: formatCost,
      },
      totalEgressCost: {
        header: 'Total egress traffic cost',
        map: formatCost,
      },
      ingressIntraAzTraffic: {
        header: 'Ingress intra AZ traffic',
        map: formatTraffic,
      },
      ingressInterAzTraffic: {
        header: 'Ingress inter AZ traffic',
        map: formatTraffic,
      },
      ingressInternetTraffic: {
        header: 'Ingress internet traffic',
        map: formatTraffic,
      },
      ingressCrossRegionTraffic: {
        header: 'Ingress cross-region traffic',
        map: formatTraffic,
      },
      ingressCloudApiTraffic: {
        header: 'Ingress cloud API traffic',
        map: formatTraffic,
      },
      totalIngressTraffic: {
        header: 'Total ingress traffic',
        map: formatTraffic,
      },
      ingressIntraAzCost: {
        header: 'Ingress intra AZ traffic cost',
        map: formatCost,
      },
      ingressInterAzCost: {
        header: 'Ingress inter AZ traffic cost',
        map: formatCost,
      },
      ingressInternetCost: {
        header: 'Ingress internet traffic cost',
        map: formatCost,
      },
      ingressCrossRegionCost: {
        header: 'Ingress cross-region traffic cost',
        map: formatCost,
      },
      ingressCloudApiCost: {
        header: 'Ingress cloud API traffic cost',
        map: formatCost,
      },
      totalIngressCost: {
        header: 'Total ingress traffic cost',
        map: formatCost,
      },
      totalTraffic: {
        header: 'Total traffic',
        map: formatTraffic,
      },
      totalCost: {
        header: 'Total traffic cost',
        map: formatCost,
      },
    },
    csvData
  );
};
